import {
  Log,
  User,
  UserManager,
  UserSettings,
  WebStorageStateStore,
} from 'oidc-client';
import { jwtDecode } from 'jwt-decode';
import Router from 'next/router';
import { ReactNode, useContext, useEffect, useState } from 'react';
import React from 'react';
import { setContext } from '@apollo/client/link/context';
import {
  ApolloLink,
  HttpLink,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { uniqueId } from 'lodash';

const SILENT_REFRESH_IFRAME_ID = 'SILENT_REFRESH_IFRAME_ID';
const REFRESH_TOKEN_STORAGE_KEY = 'workflow_refresh_token';

const UNAUTHENTICATED_ERROR_CODE = 403;

const isServerError = (err: Error): err is ServerError | ServerParseError => {
  return (
    'response' in err &&
    'statusCode' in err &&
    ('result' in err || 'bodyText' in err)
  );
};

const authLink = (user: UserType) =>
  setContext(async (req, { headers }) => {
    const refreshedToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    const authorizationToken = refreshedToken || user.access_token || null;

    return {
      ...headers,
      headers: {
        authorizationToken,
      },
    };
  });

const retryLink = (onNetworkError: () => void): ApolloLink => {
  return new RetryLink({
    attempts: (count, operation, error) => {
      const canRetry = !!error && count <= 1 && !!operation;

      if (!!operation && count === 2) {
        onNetworkError();
      }

      return canRetry;
    },
  });
};

const errorLink = (user: UserType): ApolloLink => {
  return onError(({ graphQLErrors, networkError, operation }) => {
    if (networkError) {
      console.error(
        `[🌏 Network error]: Operation: ${operation.operationName}, Message: ${networkError}`
      );

      if (
        isServerError(networkError) &&
        networkError.statusCode === UNAUTHENTICATED_ERROR_CODE
      ) {
        signin();
      }
    }

    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        const { message, extensions } = err;
        console.error(
          `[⚠️ GraphQL error]: Operation: ${
            operation.operationName
          }, Message: ${message}, Code: ${extensions && extensions.code}`
        );

        if (extensions && extensions.code === 'UNAUTHENTICATED') {
          signin();
        }
      }
    }
  });
};

export const apolloLinkMiddleware = (
  user: UserType,
  onNetworkError: () => void
) => {
  const tenant = user.profile.currentTenantId;

  const isNewDomain =
    window.location.origin && window.location.origin.includes('access-aps');

  const coreUri = isNewDomain
    ? process.env.GRAPHQL_API_CORE_NEW
    : process.env.GRAPHQL_API_CORE;
  const clientsUri = isNewDomain
    ? process.env.GRAPHQL_API_CLIENTS_NEW
    : process.env.GRAPHQL_API_CLIENTS;
  const templatesUri = isNewDomain
    ? process.env.GRAPHQL_API_TEMPLATES_NEW
    : process.env.GRAPHQL_API_TEMPLATES;
  const usersUri = isNewDomain
    ? process.env.GRAPHQL_API_USERS_NEW
    : process.env.GRAPHQL_API_USERS;

  const coreLink = new HttpLink({
    uri: `${coreUri}/${tenant}`,
  });
  const clientsLink = new HttpLink({
    uri: `${clientsUri}/${tenant}`,
  });
  const templatesLink = new HttpLink({
    uri: `${templatesUri}/${tenant}`,
  });
  const usersLink = new HttpLink({
    uri: `${usersUri}/${tenant}`,
  });

  return ApolloLink.from([
    errorLink(user),
    retryLink(onNetworkError),
    authLink(user),
    ApolloLink.split(
      (operation) => operation.getContext().service === 'clients',
      clientsLink,
      ApolloLink.split(
        (operation) => operation.getContext().service === 'users',
        usersLink,
        ApolloLink.split(
          (operation) => operation.getContext().service === 'templates',
          templatesLink,
          // Default: 'core'
          coreLink
        )
      )
    ),
  ]);
};

type UserType = Omit<User, 'profile'> & { profile: CognitoInfo };

interface CognitoInfo {
  currentTenantId: string;
  'custom:tenantList': string;
  'cognito:username': string;
  email: string;
  email_verified: string;
  name: string;
  sub: string;
  role: string;
  staffId: string;
  country: string;
  given_name: string;
  family_name: string;
}

const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
let APP_DOMAIN = process.env.REACT_APP_COGNITO_APP_DOMAIN;
let REDIRECT_URI = process.env.REACT_APP_COGNITO_REDIRECT_URI;
if (typeof window != 'undefined') {
  REDIRECT_URI = window.location.origin; //include protocol and port
  if (window.location.origin.includes('access-aps')) {
    APP_DOMAIN = process.env.REACT_APP_COGNITO_APP_DOMAIN_NEW;
  }
}
const REDIRECT_URI_SILENT = REDIRECT_URI + '/silent-refresh.html';
const REDIRECT_URI_SIGNOUT = REDIRECT_URI;
const MAIN_APP_ORIGIN = REDIRECT_URI;

const userManager =
  typeof window === 'undefined'
    ? (undefined as any as UserManager)
    : new UserManager({
        userStore: new WebStorageStateStore({
          prefix: 'workflow_',
          store: localStorage,
        }),
        authority: APP_DOMAIN,
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        silent_redirect_uri: REDIRECT_URI_SILENT,
        automaticSilentRenew: false,
        includeIdTokenInSilentRenew: false,
        accessTokenExpiringNotificationTime: 250,
        loadUserInfo: false,
        response_type: 'token',
        scope: 'openid profile email',
        // post_logout_redirect_uri: REDIRECT_URI_SIGNOUT,
        // stateStore: this.stateStore,
        // revokeAccessTokenOnSignout: true,
        metadata: {
          authorization_endpoint: `https://${APP_DOMAIN}/signin`,
          userinfo_endpoint: `https://${APP_DOMAIN}/oauth2/userInfo`,
          end_session_endpoint: `https://${APP_DOMAIN}/signout?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI_SIGNOUT}`,
        },
      });

if (userManager) {
  Log.logger = console;
  Log.level = Log.DEBUG;
}

export const signin = () => {
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  userManager.signinRedirect();
};

export const signout = () => {
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

  Object.entries(localStorage)
    .map((x) => x[0])
    .filter((x) => x.substring(0, 4) === 'oidc')
    .map((x) => localStorage.removeItem(x));

  userManager.signoutRedirect();
};

const getUserInfo = async () => {
  const userInfo = await userManager.getUser();
  if (userInfo) return userInfo as any as UserType;
};

const storeCredentials = async (hash: string) => {
  const parsedHash = new URLSearchParams(
    hash.substr(1) // skip the first char (#)
  );
  const profileInfo: any = jwtDecode(parsedHash.get('id_token') as string);

  profileInfo['currentTenantId'] = parsedHash.get('tenant') as string;

  profileInfo['staffId'] = parsedHash.get('staff') as string;
  profileInfo['role'] = parsedHash.get('role') as string;
  profileInfo['country'] = parsedHash.get('country') as string;
  profileInfo['name'] =
    (profileInfo['given_name'] ?? '') +
    ' ' +
    (profileInfo['family_name'] ?? ''); //fixup old token attributes, some of filed might be missing also

  const expires_at =
    parsedHash.has('expires_in') &&
    !isNaN(parseInt(parsedHash.get('expires_in') as string))
      ? Math.floor(Date.now() / 1000) +
        parseInt(parsedHash.get('expires_in') as string)
      : profileInfo['exp'];

  // leaving these here commented out, they're handy if we need them
  /*
  console.log(
    'now:%s, iss:%s, exp:%s',
    new Date(),
    new Date(profileInfo['iat'] * 1000),
    new Date(profileInfo['exp'] * 1000)
  );
  console.log(
    'token refresh at %s',
    new Date((expires_at - 250) * 1000).toLocaleTimeString()
  );
  console.log(
    'token expires at %s',
    new Date(expires_at * 1000).toLocaleTimeString()
  );
*/

  const userSettings: UserSettings = {
    id_token: parsedHash.get('id_token') as string,
    access_token: parsedHash.get('access_token') as string,
    token_type: parsedHash.get('token_type') as string,
    state: parsedHash.get('state') as string,
    expires_at,
    profile: profileInfo,
  } as UserSettings;

  userManager.settings.extraQueryParams = {};
  userManager.settings.extraQueryParams.access_token =
    parsedHash.get('access_token');

  const authenticatedUser = new User(userSettings) as any as UserType;

  // @ts-ignore
  await userManager.storeUser(authenticatedUser);
  await userManager.getUser();

  /*use this to test refresh
    let to = setTimeout(() => {
      console.log('sr test');
      silentRefresh();
      clearTimeout(to);
    }, 30000);
    */
  return authenticatedUser;
};

export async function silentRefresh() {
  return new Promise(async (resolve, reject) => {
    if (
      document.querySelectorAll(`iframe[id*=${SILENT_REFRESH_IFRAME_ID}]`)
        .length > 0
    )
      return;

    const onMessage = (ev: MessageEvent) => {
      // we got a message on the window. If it checks out, store the creds, teardown, return true
      if (!ev) {
        console.error('Auth.silentRefresh.onMessage: no event, bailing');
        return;
      }
      if (ev.origin !== MAIN_APP_ORIGIN) {
        console.error('Auth.silentRefresh.onMessage: wrong origin, bailing', {
          MAIN_APP_ORIGIN,
          'ev.origin': ev.origin,
        });
        return;
      }

      if (
        !ev.data ||
        !(typeof ev.data === 'string') ||
        !ev.data.startsWith('#')
      ) {
        console.error('Auth.silentRefresh.onMessage: wrong data, bailing');
        return;
      }
      const urlHash = ev.data;
      // wait for the data to be tucked away, then teardown with success
      storeCredentials(urlHash).then(() => teardown(true));
    };

    const onTimeout = () => {
      // we didn't get a message in time. Teardown with an error
      teardown(
        'Auth.silentRefresh.onTimeout: timeout waiting for message from iframe'
      );
    };

    // we either got a response, or we have an error, either way, clean up after ourselves and resolve/reject
    const teardown = (result: boolean | string) => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      window.document.body.removeChild(frame);

      if (typeof result === 'boolean') {
        resolve(result);
      } else if (typeof result === 'string') {
        //reject(new Error(result));
        //not sure this should be unhandled error. user just logout if no new token is avaialble, no need to force.
        console.error('Auth.silentRefresh.tearDown: error', result);
        resolve(false);
      }
    };

    const user = await userManager.getUser();
    if (!user) {
      // this is bad, we need that dang access token to get the next one
      reject(
        new Error(
          'Auth.silentRefresh: Failed to retrieve user when setting up silent redirect'
        )
      );
      return;
    }

    // make up the silent refresh url. Be nice to use some of the auth library to do this - there's a createSigninRequest
    // but heckin there's some parameters go into that
    // instead, this is based off a semi legit silent redirect url that the library made up once
    const silentRefreshUrl =
      `https://${APP_DOMAIN}/signin` +
      `?client_id=${CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI_SILENT || '')}` +
      `&response_type=token` +
      `&scope=openid%20profile%20email` +
      `&prompt=none` +
      `&access_token=${user.access_token}`;

    // make up the iframe
    const frame = window.document.createElement('iframe');
    frame.setAttribute('id', uniqueId(SILENT_REFRESH_IFRAME_ID));
    frame.style.visibility = 'hidden';
    frame.style.position = 'absolute';
    frame.style.display = 'none';
    frame.style.width = '0';
    frame.style.height = '0';
    window.document.body.appendChild(frame);

    // listen for the message
    window.addEventListener('message', onMessage, false);

    // setup a timer for the timeout
    const timer = window.setTimeout(onTimeout, 10000);

    // kick off the loading
    // console.log(
    //   'Auth.silentRefresh: loading refresh frame with: %s',
    //   silentRefreshUrl
    // );
    frame.src = silentRefreshUrl;
  });
}

type AuthStates =
  | {
      type: 'fetching' | 'unauthenticated';
      user?: UserType;
    }
  | {
      type: 'authenticated';
      user: UserType;
    };

let AuthContext = React.createContext<{ type: 'none' } | AuthStates>({
  type: 'none',
});

export function AuthProvider({ children }: { children: ReactNode }) {
  let [user, setUser] = useState<AuthStates>({ type: 'fetching' });

  useEffect(() => {
    if (window.location.hash) {
      storeCredentials(window.location.hash).then((user) => {
        setUser({ type: 'authenticated', user });
        Router.replace('/');
      });
    } else {
      getUserInfo().then((user) => {
        setUser(
          user ? { type: 'authenticated', user } : { type: 'unauthenticated' }
        );
      });
    }
    setAccessExpiring();
  }, []);

  const setAccessExpiring = async () => {
    userManager.events.addAccessTokenExpiring(async () => {
      await silentRefresh();
      const getUser = (await userManager.getUser()) as UserType | null;

      localStorage.setItem(
        REFRESH_TOKEN_STORAGE_KEY,
        getUser?.access_token || ''
      );

      getUser && setUser({ type: 'authenticated', user: getUser });
    });
  };

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  let auth = useContext(AuthContext);
  if (auth.type === 'none') {
    throw new Error('Please wrap your app in a AuthProvider');
  }
  return auth;
}

export function useUser() {
  let auth = useAuth();
  if (auth.type !== 'authenticated') {
    throw new Error('useUser can only be used when the user is authenticated');
  }
  return auth.user;
}
